import React, { useState, useEffect } from "react";
import QrReader from "react-qr-reader";
import PageTitle from "../pages/PageTitle";
import axios from "axios";
import { toast } from "react-toastify";
import LabDetailsTable from "./LabDetailsTable";
import {
    getLabDetailsAPIURL,
    getLabDetailsByUniqueIdAPIURL,
    saveLabDetailsAPIURL,
    updateLabDetailsAPIURL
} from "../commonServices/LabReportAPIURL";
import DatePicker from "react-datepicker";
import moment from "moment";
import { useSelector } from "react-redux";
import Loader from "../pages/Loader";

const LabReport = () => {
    const authUser = useSelector((state) => state.auth.user);

    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);

    const [scanInput, setScanInput] = useState("");
    const [scannerModalOpen, setScannerModalOpen] = useState(false);
    const [labModalOpen, setLabModalOpen] = useState(false);

    const [editFlag, setEditFlag] = useState(1);

    const [labData, setLabData] = useState({
        fat: "",
        density: "",
        snf: "",
        protein: "",
        dateTime: "",
        dateObj: new Date()
    });

    const toggleScannerModal = () => setScannerModalOpen(!scannerModalOpen);
    const toggleLabModal = () => {
        setLabModalOpen(!labModalOpen);
        // setScanInput('');
        setLabData(prev => ({
            ...prev,
            fat: "",
            density: "",
            snf: "",
            protein: "",
        }));
    };

    // AUTO UPDATE TIME EVERY 1 MINUTE
    useEffect(() => {
        const updateNow = () => {
            const now = new Date();
            const backendFormat = moment(now).format("YYYY-MM-DDTHH:mm");

            setLabData(prev => ({
                ...prev,
                dateObj: now,
                dateTime: backendFormat
            }));
        };

        updateNow();
        const timer = setInterval(updateNow, 60000);
        return () => clearInterval(timer);
    }, []);

    // VALIDATE UNIQUE ID
    // VALIDATE UNIQUE ID
    const validateQR = async (uniqueId) => {

        if (editFlag == 1) {
            // RESET LAB DATA IN ADD MODE
            setLabData({
                id: "",
                uniqueId: uniqueId,
                fat: "",
                density: "",
                snf: "",
                protein: "",
                dateObj: new Date(),
                dateTime: moment(new Date()).format("YYYY-MM-DDTHH:mm")
            });
        }

        if (!authUser?.token || authUser.token.trim() == "") {
            toast.error("Authorization token missing!");
            return;
        }

        if (!uniqueId || uniqueId.trim() == "") {
            toast.error("Required Unique ID");
            return;
        }

        setLoader(true);

        try {
            const res = await axios.get(
                `${getLabDetailsByUniqueIdAPIURL}?uniqueId=${uniqueId}`,
                { headers: { Authorization: `Bearer ${authUser.token}` } }
            );

            const labInfo = res.data;

            // If found → populate (for ADD MODE also)
            if (labInfo?.uniqueId) {
                // setLabData(prev => ({
                //     ...prev,
                //     fat: labInfo.fat || "",
                //     density: labInfo.density || "",
                //     snf: labInfo.snf || "",
                //     protein: labInfo.protein || ""
                // }));

                toast.success("Valid Unique ID ✔ Opening Lab Details...");
                setLabModalOpen(true);
            } else {
                toast.error("Invalid Unique ID");
            }

        } catch (err) {
            console.error(err);
            toast.error("Server Error — Unable to validate Unique ID");
        } finally {
            setLoader(false);
        }
    };


    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        if (!authUser?.token) {
            toast.error("Authorization token missing!");
            return;
        }

        setLoader(true);

        try {
            const resp = await axios.get(getLabDetailsAPIURL, {
                headers: { Authorization: `Bearer ${authUser.token}` }
            });

            if (resp.data == "NOTFOUND") {
                toast.warn("Data not found with this unique id.");
            } else {
                setData(resp.data);
            }

        } catch (error) {
            console.error("Error fetching gate entries:", error);
        } finally {
            setLoader(false);
        }
    };

    const saveLabReport = async () => {
        setLoader(true);

        if (!authUser?.token) {
            toast.error("Authorization token missing!");
            setLoader(false);
            return;
        }

        if (!scanInput) {
            toast.error("Please scan or enter Unique ID");
            setLoader(false);
            return;
        }

        if (!labData.fat || !labData.density || !labData.protein || !labData.snf) {
            toast.error("Please fill all lab details");
            setLoader(false);
            return;
        }

        try {
            const dt = labData.dateObj || new Date();

            const payload = {
                uniqueId: Number(scanInput),
                fat: Number(labData.fat),
                density: Number(labData.density),
                protein: Number(labData.protein),
                snf: Number(labData.snf),
                entryDate: moment(dt).format("YYYY-MM-DD"),
                entryTime: moment(dt).format("HH:mm:ss"),
            };

            const resp = await axios.post(
                saveLabDetailsAPIURL,
                payload,
                { headers: { Authorization: `Bearer ${authUser.token}` } }
            );

            // 🔥 NEW CHECK — ALREADY EXISTS
            if (resp.data == "ALREADYEXISTS") {
                setLoader(false);
                toast.warn("Lab Report already exists for this Unique ID!");
                return; // Do NOT proceed further
            }

            if (resp.data == "SAVED") {
                setLoader(false);
                toast.success("Lab Report Saved Successfully ✅");

                setLabModalOpen(false);
                setEditFlag(1);

                fetchData();
                setScanInput("");

                // Reset Lab fields
                setLabData(prev => ({
                    ...prev,
                    fat: "",
                    density: "",
                    snf: "",
                    protein: "",
                }));
            } else {
                setLoader(false);
                toast.error("Failed to save lab report");
            }

        } catch (err) {
            console.error(err);
            toast.error("Server Error — Unable to save");
            setLoader(false);
        }
    };


    const updateLabReport = async () => {
        if (!authUser?.token) {
            toast.error("Authorization token missing!");
            return;
        }

        if (!labData.fat || !labData.density || !labData.protein || !labData.snf) {
            toast.error("Please fill all lab details");
            return;
        }

        setLoader(true);

        try {
            const dt = labData.dateObj;

            const payload = {
                id: labData.id,                       // 👉 ID added
                uniqueId: Number(labData.uniqueId),   // 👉 UniqueId added
                fat: Number(labData.fat),
                density: Number(labData.density),
                protein: Number(labData.protein),
                snf: Number(labData.snf),
                entryDate: moment(dt).format("YYYY-MM-DD"),
                entryTime: moment(dt).format("HH:mm:ss"),
                editFlag: 2
            };

            const resp = await axios.put(updateLabDetailsAPIURL, payload, {
                headers: { Authorization: `Bearer ${authUser.token}` }
            });

            if (resp.data == "UPDATED") {
                toast.success("Lab Report Updated Successfully ✔");
                setEditFlag(1);
                setLabModalOpen(false);
                fetchData();
                setScanInput("");
                setLabData(prev => ({
                    ...prev,
                    fat: "",
                    density: "",
                    snf: "",
                    protein: "",
                }));
            } else {
                toast.error("Update failed");
            }

        } catch (err) {
            console.error(err);
            toast.error("Server error");
        }

        setLoader(false);
    };



    // OPEN CAMERA WITH PERMISSION
    const handleOpenScanner = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" }
            });

            stream.getTracks().forEach(track => track.stop());
            setScannerModalOpen(true);

        } catch (err) {
            console.error("Camera permission error:", err);
            toast.error("Camera access blocked. Please allow permission.");

            if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
                toast.info("iOS → Settings → Safari → Camera → Allow");
            }
        }
    };

    return (
        <>
            {loader ? <Loader /> : (
                <>
                    <PageTitle title="Lab Report" />

                    <div className="bg-white shadow-md rounded-xl p-6 mx-auto w-full min-h-[300px]">

                        {/* Input Section */}
                        {authUser?.roleId !== 2 && (
                            <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:gap-3 gap-2 w-full">

                                <input
                                    type="text"
                                    className="border px-4 py-2 rounded-lg w-full sm:w-64 shadow-sm"
                                    placeholder="Unique Id"
                                    value={scanInput}
                                    onChange={(e) => setScanInput(e.target.value)}
                                    onBlur={() => {
                                        setEditFlag(1);       // ADD mode
                                        validateQR(scanInput);
                                    }}
                                />

                                <button
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg shadow w-full sm:w-auto"
                                    onClick={handleOpenScanner}
                                >
                                    📷 Scan here
                                </button>
                            </div>
                        )}

                        {/* Table */}
                        {data.length !== 0 && (
                            <div className={authUser?.roleId == 2 ? "" : "mt-6"}>
                                <LabDetailsTable
                                    data={data}
                                    setData={setData}
                                    authUser={authUser}
                                    loader={loader}
                                    setLoader={setLoader}
                                    setEditFlag={setEditFlag}
                                    setLabData={setLabData}
                                    setLabModalOpen={setLabModalOpen}
                                    setScanInput={setScanInput}
                                />
                            </div>
                        )}

                        {/* QR Scanner Modal */}
                        {scannerModalOpen && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                                <div className="bg-white p-5 rounded-xl shadow-xl w-[340px]">

                                    <div className="flex justify-between items-center mb-3">
                                        <h2 className="text-lg font-bold">Scan QR Code</h2>
                                        <button
                                            className="text-red-500 font-bold"
                                            onClick={toggleScannerModal}
                                        >
                                            ✖
                                        </button>
                                    </div>

                                    <div className="w-full h-[280px] rounded-lg overflow-hidden border border-gray-300">
                                        <QrReader
                                            delay={300}
                                            onError={(err) => console.error("QR ERROR:", err)}
                                            onScan={async (result) => {
                                                if (result) {
                                                    let scannedValue =
                                                        typeof result == "string"
                                                            ? result
                                                            : result.text || result.data || "";

                                                    if (!scannedValue) return;

                                                    if (scanInput == scannedValue) return;

                                                    setScanInput(scannedValue);
                                                    setScannerModalOpen(false);

                                                    setTimeout(() => {
                                                        setEditFlag(1);       // ADD mode
                                                        validateQR(scannedValue);

                                                    }, 300);
                                                }
                                            }}
                                            style={{ width: "100%", height: "100%" }}
                                        />
                                    </div>

                                </div>
                            </div>
                        )}

                        {/* Modal for Lab Entry */}
                        {labModalOpen && (
                            <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-center z-50">
                                <div className="bg-white p-6 rounded-2xl w-[540px] shadow-2xl border border-gray-100">

                                    <div className="flex justify-between items-center mb-4 pb-3 border-b">
                                        <h2 className="text-2xl font-semibold text-gray-800">🧪 Lab Report Entry</h2>
                                        <button
                                            className="text-red-500 text-2xl font-bold hover:text-red-700"
                                            onClick={toggleLabModal}
                                        >
                                            ✖
                                        </button>
                                    </div>

                                    {/* FORM */}
                                    <div className="grid grid-cols-2 gap-4">

                                        {/* DATE & TIME */}
                                        <div className="col-span-2">
                                            <label className="text-sm font-medium text-gray-600">Date & Time</label>

                                            <DatePicker
                                                selected={labData.dateObj}
                                                onChange={(dt) => {
                                                    const format = moment(dt).format("YYYY-MM-DDTHH:mm");
                                                    setLabData(prev => ({
                                                        ...prev,
                                                        dateObj: dt,
                                                        dateTime: format,
                                                    }));
                                                }}
                                                showTimeSelect
                                                timeIntervals={1}
                                                dateFormat="dd/MM/yyyy HH:mm"
                                                customInput={
                                                    <input
                                                        className="border rounded-lg p-2 w-full bg-white"
                                                        value={moment(labData.dateObj).format("DD/MM/YYYY HH:mm")}
                                                        readOnly
                                                    />
                                                }
                                            />
                                        </div>

                                        {/* Fat */}
                                        <div>
                                            <label className="text-sm">Fat %</label>
                                            <input
                                                type="number"
                                                className="border rounded-lg p-2 w-full"
                                                value={labData.fat}
                                                onChange={(e) => setLabData({ ...labData, fat: e.target.value })}
                                            />
                                        </div>

                                        {/* Density */}
                                        <div>
                                            <label className="text-sm">Density</label>
                                            <input
                                                type="number"
                                                className="border rounded-lg p-2 w-full"
                                                value={labData.density}
                                                onChange={(e) => setLabData({ ...labData, density: e.target.value })}
                                            />
                                        </div>

                                        {/* SNF */}
                                        <div>
                                            <label className="text-sm">SNF %</label>
                                            <input
                                                type="number"
                                                className="border rounded-lg p-2 w-full"
                                                value={labData.snf}
                                                onChange={(e) => setLabData({ ...labData, snf: e.target.value })}
                                            />
                                        </div>

                                        {/* Protein */}
                                        <div>
                                            <label className="text-sm">Protein %</label>
                                            <input
                                                type="number"
                                                className="border rounded-lg p-2 w-full"
                                                value={labData.protein}
                                                onChange={(e) => setLabData({ ...labData, protein: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        className={`${editFlag == 2 ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"
                                            } text-white w-full py-3 mt-6 rounded-xl text-lg font-semibold`}
                                        onClick={editFlag == 2 ? updateLabReport : saveLabReport}
                                    >
                                        {editFlag == 2 ? "Update Lab Report" : "Save Lab Report"}
                                    </button>


                                </div>
                            </div>
                        )}

                    </div>

                </>
            )}
        </>
    );
};

export default LabReport;
